import { Controller } from "@hotwired/stimulus"

/**
 * iOS Advisory Controller
 *
 * Manages the per-day dismissal of the iOS call-awareness advisory banner.
 *
 * Apple does not allow third-party iOS apps to record cellular calls, so
 * automated call capture is Android-only. We show iOS users a banner
 * explaining the limitation. The user can dismiss it — we store an
 * ISO date (`YYYY-MM-DD`) in localStorage and re-hide the banner on
 * subsequent renders within the same day. The advisory comes back the
 * next day so users are reminded periodically.
 *
 * @example
 *   <div data-controller="mobile--ios-advisory">
 *     <button data-action="click->mobile--ios-advisory#dismiss">x</button>
 *   </div>
 */
const STORAGE_KEY = "conek.calls.ios_advisory_dismissed"

function today() {
  return new Date().toISOString().slice(0, 10)
}

export default class extends Controller {
  connect() {
    try {
      const dismissedOn = window.localStorage.getItem(STORAGE_KEY)
      if (dismissedOn && dismissedOn === today()) {
        this.element.classList.add("hidden")
        this.element.setAttribute("aria-hidden", "true")
      }
    } catch (_e) {
      // localStorage can throw in private browsing / Safari sandbox.
      // Fall through and just show the banner.
    }
  }

  dismiss(event) {
    if (event) {
      event.preventDefault()
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, today())
    } catch (_e) {
      // Ignore — best-effort persistence.
    }
    this.element.style.transition = "opacity 150ms ease-out"
    this.element.style.opacity = "0"
    window.setTimeout(() => {
      this.element.classList.add("hidden")
      this.element.setAttribute("aria-hidden", "true")
      this.element.style.opacity = ""
    }, 160)
  }
}
